import * as Checkbox from "@radix-ui/react-checkbox";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import * as Dialog from "@radix-ui/react-dialog";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Check, CheckCircle2, ChevronDown, Loader2, Warehouse, CircleDashed, Clock3, XCircle, ChevronLeft, ChevronRight, DownloadCloud, FileUp, Minus, Package, Pencil, Plus, Search, ShieldCheck, SlidersHorizontal, X } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";
import { PageHeader, useTenant } from "@/components/AppShell";
import { BackendImportBanner, BackendImportButton } from "@/components/BackendImport";
import { CreateProductDialog } from "@/components/CreateProductDialog";
import { CsvImportDialog } from "@/components/CsvImportDialog";
import { ProductSheet } from "@/components/ProductSheet";
import { Badge, Button, Card, cx, EmptyState, Input, Kbd, ScoreBar, SeverityIcon, Skeleton, Tip, VALIDATION_META } from "@/components/ui";
import { api, type BackendFilter, type ProductItem, type ProductPatch, type ProductQuery, type ValidationState } from "@/lib/api";
import { fmtNumber, fmtRelative, ruleLabel } from "@/lib/format";
import { t, msg } from "@/lib/i18n";
import { SameGtin } from "@/components/SameGtin";

import { serverText } from "@/lib/server-text";
const PAGE_SIZE = 50;
const TONE_TEXT: Record<ValidationState, string> = { valid: "text-success", warnings: "text-warning", invalid: "text-danger", unchecked: "text-fg-subtle" };

export function ProductsPage() {
  const { tenantId } = useTenant();
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const searchRef = useRef<HTMLInputElement>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [searchInput, setSearchInput] = useState(params.get("search") ?? "");

  const q: ProductQuery = {
    search: params.get("search") ?? undefined,
    validation: (params.get("validation") as ValidationState | null) ?? undefined,
    rule: params.get("rule") ?? undefined,
    weightReview: params.get("weightReview") === "1" ? "1" : undefined,
    backend: (params.get("backend") as BackendFilter | null) ?? undefined,
    sort: (params.get("sort") as ProductQuery["sort"]) ?? "updatedAt",
    dir: (params.get("dir") as "asc" | "desc") ?? "desc",
    page: Number(params.get("page") ?? 1),
    pageSize: PAGE_SIZE,
  };
  const openId = params.get("open");
  const [createOpen, setCreateOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [lagerOpen, setLagerOpen] = useState(false);
  // Bearbeitungsmodus: erst dann sind Zellen beschreibbar (und Ausfüllen per Herunterziehen möglich)
  const [editMode, setEditMode] = useState(false);
  const [extras, setExtrasState] = useState<ExtraKey[]>(() => {
    try {
      const v = JSON.parse(localStorage.getItem("lc-product-cols") ?? "null") as ExtraKey[] | null;
      return Array.isArray(v) ? v.filter((k) => k in EXTRA) : DEFAULT_EXTRAS;
    } catch {
      return DEFAULT_EXTRAS;
    }
  });
  const setExtras = (v: ExtraKey[]) => { setExtrasState(v); try { localStorage.setItem("lc-product-cols", JSON.stringify(v)); } catch { /* egal */ } };
  const shownExtras = EXTRA_ORDER.filter((k) => extras.includes(k));
  const [packOpen, setPackOpen] = useState(false);
  const [fill, setFill] = useState<FillDrag | null>(null);
  const fillRef = useRef<FillDrag | null>(null);
  fillRef.current = fill;

  const update = (patch: Record<string, string | null>, resetPage = true) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) (v === null || v === "" ? next.delete(k) : next.set(k, v));
    if (resetPage && !("page" in patch)) next.delete("page");
    setParams(next, { replace: true });
  };

  // Suche entprellt in die URL (teilbare Links, Zurück-Button funktioniert)
  useEffect(() => {
    const t = setTimeout(() => (searchInput !== (params.get("search") ?? "") ? update({ search: searchInput || null }) : undefined), 220);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // "/" fokussiert die Suche
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (e.key === "/" && tag !== "INPUT" && tag !== "TEXTAREA") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const list = useQuery({ queryKey: ["products", tenantId, q], queryFn: () => api.products.list(tenantId, q), placeholderData: keepPreviousData });
  const summary = useQuery({ queryKey: ["products-summary", tenantId], queryFn: () => api.products.summary(tenantId) });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["products", tenantId] });
    void qc.invalidateQueries({ queryKey: ["products-summary", tenantId] });
    void qc.invalidateQueries({ queryKey: ["product", tenantId] });
  };

  const patch = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: ProductPatch }) => api.products.patch(tenantId, id, patch),
    onSuccess: () => invalidate(),
    onError: () => toast.error("Speichern fehlgeschlagen – bitte erneut versuchen"),
  });

  const importMut = useMutation({
    mutationFn: () => api.products.import(tenantId),
    onSuccess: (r) => {
      if (r.queued) toast.success("Import gestartet", { description: msg("Neue Artikel erscheinen nach und nach – große Sortimente brauchen einige Minuten.") });
      for (const c of r.needsFilter ?? [])
        toast.warning(`${c.name}: großes Sortiment`, { description: msg("Bitte zuerst festlegen, welche Artikel ins Lager sollen."), action: { label: msg("Auswahl öffnen"), onClick: () => location.assign(`/channels/${c.id}`) }, duration: 12_000 });
      if (!r.queued && !r.needsFilter?.length) toast.info("Kein verbundener Shop");
    },
  });

  const total = summary.data ? Object.values(summary.data.byStatus).reduce((a, b) => a + (b ?? 0), 0) : undefined;
  const rawItems = list.data?.items ?? [];
  // Bearbeitungsmodus: Reihenfolge einfrieren – sonst springen geänderte Zeilen (Sortierung „zuletzt geändert“) beim Bearbeiten weg
  const frozen = useRef<string[] | null>(null);
  if (!editMode) frozen.current = null;
  else if (!frozen.current || rawItems.some((x) => !frozen.current!.includes(x.id))) frozen.current = [...(frozen.current ?? []), ...rawItems.map((x) => x.id).filter((id) => !frozen.current?.includes(id))];
  const items = editMode && frozen.current ? [...rawItems].sort((x, y) => frozen.current!.indexOf(x.id) - frozen.current!.indexOf(y.id)) : rawItems;
  const allSelected = items.length > 0 && items.every((p) => selected.has(p.id));
  const pages = list.data ? Math.max(1, Math.ceil(list.data.total / PAGE_SIZE)) : 1;

  const toggleSort = (key: NonNullable<ProductQuery["sort"]>) =>
    update({ sort: key, dir: q.sort === key && q.dir === "asc" ? "desc" : "asc" }, false);

  // Maus losgelassen (auch außerhalb der Tabelle) → Ausfüllen anwenden
  useEffect(() => {
    const up = () => { const f = fillRef.current; if (f) { setFill(null); void applyFill(f); } };
    window.addEventListener("mouseup", up);
    return () => window.removeEventListener("mouseup", up);
  });

  // Filter „Doppelte EAN“: Gruppen gleicher EAN (Liste ist danach sortiert) für die Streifen-Markierung
  const gtinGroups = new Map<string, number>();
  {
    let g = -1, last: string | null = null;
    for (const p of items) {
      const e = p.identifiers.gtin ?? null;
      if (e !== last) { g++; last = e; }
      gtinGroups.set(p.id, g);
    }
  }

  return (
    <div className="pb-24">
      <PageHeader
        title={t("Artikel")}
        description={total !== undefined ? t("{total} Artikel · durchschnittliche Datenqualität {averageCompleteness}%", { total: fmtNumber(total), averageCompleteness: summary.data?.averageCompleteness ?? 0 }) : t("Stammdaten aus deinen Shops – geprüft, bevor sie ins Lager gehen")}
        actions={
          <div className="flex flex-wrap gap-2">
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <Button icon={importMut.isPending ? <Loader2 className="size-4 animate-spin" /> : <DownloadCloud className="size-4" />}>{t("Importieren")} <ChevronDown className="size-3.5 -mr-1 opacity-60" /></Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content align="end" sideOffset={6} className="z-[60] min-w-[260px] rounded-xl border border-border bg-surface p-1 shadow-xl animate-fade-in">
                  {([
                    [<FileUp key="i" className="size-4 mt-0.5 text-fg-muted" />, msg("CSV-Datei"), msg("Eigene Liste oder ERP-Export"), () => setCsvOpen(true)],
                    [<DownloadCloud key="i" className="size-4 mt-0.5 text-fg-muted" />, msg("Aus Shopify"), msg("Neue Varianten aus verbundenen Shops"), () => importMut.mutate()],
                    [<Warehouse key="i" className="size-4 mt-0.5 text-fg-muted" />, msg("Aus dem Lager (FFN)"), msg("Artikel aus dem JTL-FFN-Konto übernehmen"), () => setLagerOpen(true)],
                  ] as const).map(([icon, title, sub, onSelect]) => (
                    <DropdownMenu.Item key={title} onSelect={onSelect} className="flex items-start gap-2.5 rounded-lg px-2.5 py-2 text-[13px] outline-none cursor-pointer data-[highlighted]:bg-surface-2">
                      {icon}
                      <span><span className="block font-medium">{t(title)}</span><span className="block text-[11.5px] text-fg-subtle">{t(sub)}</span></span>
                    </DropdownMenu.Item>
                  ))}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
            <BackendImportButton hideTrigger open={lagerOpen} onOpenChange={setLagerOpen} />
            <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreateOpen(true)}>{t("Artikel anlegen")}</Button>
          </div>
        }
      />

      <div className="px-4 md:px-8 space-y-3">
        <BackendImportBanner />
        {/* Suche: groß und über die volle Breite (wie bei Aufträgen) */}
        <Input
          ref={searchRef}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={t("Artikel suchen: SKU, Name oder EAN …")}
          aria-label={t("Artikel durchsuchen")}
          leading={<Search className="size-4" />}
          suffix={searchInput ? <button onClick={() => setSearchInput("")} className="size-6 grid place-items-center rounded-md text-fg-subtle hover:bg-surface-2 hover:text-fg" aria-label={t("Suche leeren")}><X className="size-3.5" /></button> : <Kbd>/</Kbd>}
          className="h-11 text-[14px]"
        />
        {/* Status-Tabs */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex p-0.5 rounded-lg bg-surface-2 border border-border" role="tablist">
            {([undefined, "invalid", "warnings", "valid"] as const).map((s) => {
              const count = s ? summary.data?.byStatus[s] : total;
              const active = q.validation === s;
              const meta = s ? VALIDATION_META[s] : null;
              return (
                <button
                  key={s ?? "all"}
                  role="tab"
                  aria-selected={active}
                  onClick={() => update({ validation: s ?? null, rule: null })}
                  className={cx("h-7 px-3 rounded-md text-[12.5px] font-medium flex items-center gap-1.5 transition-colors", active ? "bg-surface shadow-sm text-fg" : "text-fg-muted hover:text-fg")}
                >
                  {meta && <meta.Icon className={cx("size-3.5", TONE_TEXT[s!])} />}
                  {s ? meta!.label : t("Alle")}
                  <span className="text-fg-subtle tabular-nums">{count ?? "–"}</span>
                </button>
              );
            })}
          </div>
          {/* Lager-Stand und häufigste Probleme als kompakte Auswahl statt Chip-Reihen */}
          <FilterSelect
            label={t("Im Lager")}
            value={q.backend ?? ""}
            onChange={(v) => update({ backend: v || null })}
            options={BACKEND_FILTERS.map((f) => ({ value: f.key, label: f.label, count: summary.data?.backendCounts?.[f.key] }))}
          />
          {summary.data && summary.data.topIssues.length > 0 && (
            <FilterSelect
              label={t("Problem")}
              value={q.rule ?? ""}
              onChange={(v) => update({ rule: v || null, validation: null })}
              options={problemOptions(summary.data.topIssues)}
            />
          )}
          {q.weightReview && (
            <button onClick={() => update({ weightReview: null })} className="h-8 px-2.5 rounded-lg border border-ink bg-ink text-ink-fg text-[12.5px] font-medium inline-flex items-center gap-1.5">{t("Gewicht stark geändert")} <X className="size-3.5" /></button>
          )}
          {(q.backend || q.rule || q.validation || q.search || q.weightReview) && (
            <button onClick={() => { setSearchInput(""); setParams({}); }} className="text-[12.5px] text-fg-muted hover:text-fg underline underline-offset-2">{t("Filter zurücksetzen")}</button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {editMode ? (
            <span className="text-[12.5px] text-fg-muted">{t("Zelle anklicken zum Ändern · Ecke")} <span className="inline-block size-2 bg-ink rounded-[1px] align-middle" /> {t("herunterziehen, um den Wert in weitere Zeilen zu übernehmen (Doppelklick: bis zum Ende der Seite)")}</span>
          ) : (
            <span className="text-[12.5px] text-fg-muted tabular-nums">{list.data ? t("{v} Artikel", { v: fmtNumber(list.data.total) }) : ""}</span>
          )}
          {editMode && <ColumnPicker value={extras} onChange={setExtras} />}
          <Button size="sm" variant={editMode ? "primary" : "ghost"} icon={editMode ? <Check className="size-3.5" /> : <Pencil className="size-3.5" />} onClick={() => { setEditMode(!editMode); setFill(null); }} className={editMode ? "" : "ml-auto"}>
            {editMode ? t("Fertig") : t("Bearbeiten")}
          </Button>
        </div>
        <Card className={cx("overflow-hidden", editMode && "ring-2 ring-ink/15", fill && "select-none")}>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead className="text-[12px] text-fg-subtle font-medium border-b border-border bg-surface-2/50">
                <tr>
                  <th className="w-10 pl-4">
                    <SelectBox
                      checked={allSelected ? true : items.some((p) => selected.has(p.id)) ? "indeterminate" : false}
                      onChange={(v) => setSelected(v ? new Set([...selected, ...items.map((p) => p.id)]) : new Set([...selected].filter((id) => !items.some((p) => p.id === id))))}
                      label={t("Alle auf dieser Seite auswählen")}
                    />
                  </th>
                  <Th onClick={() => toggleSort("sku")} sorted={q.sort === "sku" ? q.dir : undefined}>{t("Artikel")}</Th>
                  <Th>{t("EAN")}</Th>
                  <Th className="text-right">{t("Gewicht")}</Th>
                  <Th>{t("Maße L × B × H")}</Th>
                  {editMode ? (
                    <>
                      {shownExtras.map((k) => <Th key={k} className={EXTRA[k].align === "right" ? "text-right" : undefined}>{t(EXTRA[k].label)}</Th>)}
                      <Th>{t("Prüfung")}</Th>
                    </>
                  ) : (
                    <>
                      <Th onClick={() => toggleSort("completenessScore")} sorted={q.sort === "completenessScore" ? q.dir : undefined}>{t("Qualität")}</Th>
                      <Th>{t("Prüfung")}</Th>
                      <Th>{t("Lager")}</Th>
                      <Th onClick={() => toggleSort("updatedAt")} sorted={q.sort === "updatedAt" ? q.dir : undefined} className="pr-4 text-right hidden 2xl:table-cell">{t("Geändert")}</Th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {list.isLoading &&
                  Array.from({ length: 10 }, (_, i) => (
                    <tr key={i} className="border-b border-border last:border-0">
                      <td className="pl-4 py-3"><Skeleton className="size-4" /></td>
                      <td className="py-3"><Skeleton className="h-4 w-56" /></td>
                      {Array.from({ length: 6 }, (_, j) => <td key={j} className="py-3 px-3"><Skeleton className="h-4 w-20" /></td>)}
                    </tr>
                  ))}
                {items.map((p, index) => (
                  <ProductRow
                    key={p.id}
                    p={p}
                    gtinGroup={q.rule === "gtin.unique" ? gtinGroups.get(p.id) : undefined}
                    onOpenId={(id) => update({ open: id }, false)}
                    index={index}
                    editMode={editMode}
                    extras={shownExtras}
                    fill={fill}
                    onFillStart={(col, value) => setFill({ col, from: index, to: index, value })}
                    onFillOver={() => fill && setFill({ ...fill, to: index })}
                    onFillToEnd={(col, value) => void applyFill({ col, from: index, to: items.length - 1, value })}
                    selected={selected.has(p.id)}
                    onSelect={(v) => {
                      const next = new Set(selected);
                      v ? next.add(p.id) : next.delete(p.id);
                      setSelected(next);
                    }}
                    onOpen={() => update({ open: p.id }, false)}
                    onPatch={(patch) => patch && Object.keys(patch).length && list.data && patchAndToast(p, patch)}
                    active={openId === p.id}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {!list.isLoading && items.length === 0 && (
            <EmptyState icon={<Package className="size-5" />} title={q.search || q.rule || q.validation ? t("Keine Artikel für diesen Filter") : t("Noch keine Artikel")} action={q.search || q.rule || q.validation ? <Button onClick={() => { setSearchInput(""); setParams({}); }}>{t("Filter zurücksetzen")}</Button> : undefined}>
              {q.validation === "invalid" && !q.search ? t("Sehr gut – kein Artikel blockiert die Übertragung ins Lager.") : t("Verbinde einen Shop oder starte den Import, um Artikel zu übernehmen.")}
            </EmptyState>
          )}
          {list.data && list.data.total > 0 && (
            <div className="flex items-center gap-3 px-4 h-11 border-t border-border text-[12.5px] text-fg-muted">
              <span>
                {(q.page! - 1) * PAGE_SIZE + 1}–{Math.min(q.page! * PAGE_SIZE, list.data.total)} {t("von")} {fmtNumber(list.data.total)}
              </span>
              {list.isFetching && <span className="size-3 rounded-full border-2 border-fg-subtle border-r-transparent animate-spin" aria-label={t("Lädt")} />}
              <div className="ml-auto flex items-center gap-1">
                <Button size="sm" variant="ghost" disabled={q.page! <= 1} onClick={() => update({ page: String(q.page! - 1) }, false)} aria-label={t("Vorherige Seite")}>
                  <ChevronLeft className="size-4" />
                </Button>
                <span className="tabular-nums">{t("Seite")} {q.page} / {pages}</span>
                <Button size="sm" variant="ghost" disabled={q.page! >= pages} onClick={() => update({ page: String(q.page! + 1) }, false)} aria-label={t("Nächste Seite")}>
                  <ChevronRight className="size-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Aktionsleiste bei Auswahl */}
      {selected.size > 0 && (
        <div className="fixed bottom-6 left-1/2 md:left-[calc(50%+120px)] -translate-x-1/2 z-30 flex items-center gap-2 pl-4 pr-2 h-12 max-w-[calc(100vw-32px)] whitespace-nowrap rounded-xl bg-ink text-ink-fg shadow-2xl animate-pop-in">
          <span className="text-[13px] font-medium tabular-nums">{selected.size} {t("ausgewählt")}</span>
          <span className="w-px h-5 bg-ink-fg/20 mx-1" />
          <button onClick={() => setBulkOpen(true)} className="h-8 px-3 rounded-lg hover:bg-ink-fg/10 text-[13px] font-medium flex items-center gap-1.5">
            <Pencil className="size-3.5" /> {t("Feld setzen")}
          </button>
          <button onClick={() => setPackOpen(true)} className="h-8 px-3 rounded-lg hover:bg-ink-fg/10 text-[13px] font-medium flex items-center gap-1.5">
            <Package className="size-3.5" /> {t("Gebinde")}
          </button>
          <button onClick={() => void releaseSelected()} disabled={releasing} className="h-8 px-3 rounded-lg hover:bg-ink-fg/10 text-[13px] font-medium flex items-center gap-1.5 disabled:opacity-60">
            <ShieldCheck className="size-3.5" /> {t("Für Logiful freigeben")}
          </button>
          <button onClick={() => setSelected(new Set())} className="h-8 px-3 rounded-lg hover:bg-ink-fg/10 text-[13px]">{t("Auswahl aufheben")}</button>
        </div>
      )}

      <BulkEditDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        count={selected.size}
        onApply={async (patch) => {
          const r = await api.products.bulk(tenantId, [...selected], patch);
          invalidate();
          setSelected(new Set());
          toast.success(`${r.changed} Artikel aktualisiert und neu geprüft`);
          if (r.blocked?.length) toast.warning(`${r.blocked.length} Artikel nicht geändert`, { description: `Charge/MHD/Stückliste gesperrt, solange das Lager Bestand, Anlieferungen oder Aufträge hat: ${r.blocked.slice(0, 5).map((b) => b.sku).join(", ")}${r.blocked.length > 5 ? " …" : ""}` });
        }}
      />

      <PackUnitBulkDialog
        open={packOpen}
        onOpenChange={setPackOpen}
        count={selected.size}
        onApply={async (mode, unit) => {
          const r = await api.products.bulkPackUnit(tenantId, [...selected], mode, unit);
          invalidate();
          setSelected(new Set());
          toast.success(r.changed ? `Gebinde „${unit.name}“ bei ${r.changed} Artikeln ${mode === "remove" ? "entfernt" : mode === "replace" ? "gesetzt" : "ergänzt"}` : "Keine Änderung nötig", { description: r.changed && mode !== "remove" ? "Eigene EANs je Gebinde am Artikel ergänzen." : undefined });
        }}
      />

      <CsvImportDialog kind="products" open={csvOpen} onOpenChange={setCsvOpen} onDone={invalidate} />
      <CreateProductDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={(id) => { invalidate(); update({ open: id }, false); }} />
      <ProductSheet productId={openId} onClose={() => update({ open: null }, false)} onSaved={invalidate} onOpenProduct={(id) => update({ open: id }, false)} />
    </div>
  );

  async function releaseSelected() {
    setReleasing(true);
    try {
      const r = await api.products.release(tenantId, [...selected]);
      invalidate();
      setSelected(new Set());
      const parts = [r.alreadyReleased ? `${r.alreadyReleased} bereits freigegeben` : "", r.notTransferred.length ? `${r.notTransferred.length} noch nicht im Lager` : ""].filter(Boolean);
      if (r.queued) toast.success(`${r.queued} Artikel werden für Logiful freigegeben`, parts.length ? { description: parts.join(" · ") } : undefined);
      else toast.info("Nichts freizugeben", { description: parts.join(" · ") || undefined });
      if (r.notTransferred.length) toast.warning(`${r.notTransferred.length} Artikel nicht freigegeben`, { description: r.notTransferred.slice(0, 5).map((n) => `${n.sku}: ${serverText(n.reason)}`).join(" · ") + (r.notTransferred.length > 5 ? " …" : "") });
    } catch {
      toast.error("Freigabe fehlgeschlagen");
    } finally {
      setReleasing(false);
    }
  }

  /** Herunterziehen abschließen: Wert in die Zeilen übernehmen, mit „Rückgängig“ */
  async function applyFill(f: FillDrag) {
    const lo = Math.min(f.from, f.to), hi = Math.max(f.from, f.to);
    const targets = items.slice(lo, hi + 1).filter((_, i) => lo + i !== f.from);
    if (!targets.length) return;
    const col = COLS[f.col];
    const patchData = col.toPatch(f.value);
    if (typeof patchData === "string") { toast.error(patchData); return; }
    const before = targets.map((t) => ({ id: t.id, patch: col.toPatch(col.get(t)) }));
    try {
      const r = await api.products.bulk(tenantId, targets.map((t) => t.id), patchData);
      invalidate();
      toast.success(`${col.label} „${f.value || "leer"}“ in ${r.changed} Artikel übernommen`, {
        description: r.blocked?.length ? `${r.blocked.length} gesperrt` : undefined,
        action: {
          label: msg("Rückgängig"),
          onClick: () => void Promise.all(before.map((b) => (typeof b.patch === "string" ? null : api.products.patch(tenantId, b.id, b.patch)))).then(() => { invalidate(); toast.success("Rückgängig gemacht"); }),
        },
        duration: 10_000,
      });
    } catch {
      toast.error("Übernehmen fehlgeschlagen");
    }
  }

  function patchAndToast(p: ProductItem, patchData: ProductPatch) {
    const before = p.validation.status;
    patch.mutate(
      { id: p.id, patch: patchData },
      {
        onSuccess: async () => {
          const fresh = await api.products.get(tenantId, p.id);
          const after = fresh.validation.status;
          if (before === "invalid" && after !== "invalid") toast.success(`${p.sku} ist jetzt freigegeben`, { description: msg("Der Artikel wird ans Lager übertragen.") });
          else toast.success("Gespeichert", { description: after === "invalid" ? `${fresh.validation.issues.filter((i) => i.severity === "error").length} Fehler offen` : undefined });
        },
      },
    );
  }
}

/** Bearbeitbare Spalten: Anzeige-Wert ↔ Änderung (gleich für Zelle, Herunterziehen und Rückgängig) */
type ColKey = "gtin" | "weight" | "dims" | "origin" | "hs" | "name" | "mpn" | "manufacturer" | "netWeight" | "customsValue" | "customsDesc";
const kgOf = (v: string, label: string): number | null | string => {
  if (!v) return null;
  const n = Number(v.replace(",", "."));
  return Number.isFinite(n) && n > 0 ? n : `${label} bitte als Zahl in kg, z. B. 0,35`;
};
const COLS: Record<ColKey, { label: string; get: (p: ProductItem) => string; toPatch: (v: string) => ProductPatch | string }> = {
  gtin: { label: msg("EAN"), get: (p) => p.identifiers.gtin ?? "", toPatch: (v) => ({ gtin: v || null }) },
  weight: {
    label: msg("Gewicht"),
    get: (p) => (p.weight?.grossKg != null ? String(p.weight.grossKg).replace(".", ",") : ""),
    toPatch: (v) => {
      if (!v) return { grossWeightKg: null };
      const n = Number(v.replace(",", "."));
      return Number.isFinite(n) && n > 0 ? { grossWeightKg: n } : "Gewicht bitte als Zahl in kg, z. B. 0,35";
    },
  },
  dims: {
    label: msg("Maße"),
    get: (p) => { const d = p.dimensions; return d && d.lengthCm && d.widthCm && d.heightCm ? `${fmtNumber(d.lengthCm)} × ${fmtNumber(d.widthCm)} × ${fmtNumber(d.heightCm)}` : ""; },
    toPatch: (v) => {
      if (!v) return { lengthCm: null, widthCm: null, heightCm: null };
      const parts = v.split(/[x×*\s]+/i).map((x) => Number(x.replace(/\./g, "").replace(",", "."))).filter((n) => !Number.isNaN(n) && n > 0);
      return parts.length === 3 ? { lengthCm: parts[0]!, widthCm: parts[1]!, heightCm: parts[2]! } : "Bitte drei Werte eingeben, z. B. 30 × 20 × 5";
    },
  },
  origin: {
    label: msg("Ursprungsland"),
    get: (p) => p.customs?.countryOfOrigin ?? "",
    toPatch: (v) => (!v ? { countryOfOrigin: null } : /^[A-Za-z]{2}$/.test(v) ? { countryOfOrigin: v.toUpperCase() } : "Ursprungsland als ISO-Code, z. B. DE, CN"),
  },
  hs: {
    label: msg("Zolltarifnummer"),
    get: (p) => p.customs?.hsCode ?? "",
    toPatch: (v) => { const d = v.replace(/[\s.]/g, ""); return !d ? { hsCode: null } : /^\d{6,11}$/.test(d) ? { hsCode: d } : "Zolltarifnummer: 6–11 Ziffern"; },
  },
  name: { label: msg("Name"), get: (p) => p.name, toPatch: (v) => (v ? { name: v } : "Name darf nicht leer sein") },
  mpn: { label: msg("Hersteller-Art.-Nr."), get: (p) => p.identifiers.mpn ?? "", toPatch: (v) => ({ mpn: v || null }) },
  manufacturer: { label: msg("Hersteller"), get: (p) => p.identifiers.manufacturer ?? "", toPatch: (v) => ({ manufacturer: v || null }) },
  netWeight: {
    label: msg("Nettogewicht"),
    get: (p) => (p.weight?.netKg != null ? String(p.weight.netKg).replace(".", ",") : ""),
    toPatch: (v) => { const r = kgOf(v, "Nettogewicht"); return typeof r === "string" ? r : { netWeightKg: r }; },
  },
  customsValue: {
    label: msg("Zollwert"),
    get: (p) => (p.customs?.customsValue ? (p.customs.customsValue.amountMinor / 100).toFixed(2).replace(".", ",") : ""),
    toPatch: (v) => { if (!v) return { customsValueMinor: null }; const n = Number(v.replace(/\./g, "").replace(",", ".")); return Number.isFinite(n) && n >= 0 ? { customsValueMinor: Math.round(n * 100) } : "Zollwert als Betrag, z. B. 12,90"; },
  },
  customsDesc: { label: msg("Zollbeschreibung"), get: (p) => p.customs?.customsDescription ?? "", toPatch: (v) => ({ customsDescription: v || null }) },
};

/** Zusatzspalten im Bearbeitungsmodus – frei wählbar (je Browser gemerkt) */
type ExtraKey = "origin" | "hs" | "name" | "mpn" | "manufacturer" | "netWeight" | "customsValue" | "customsDesc" | "batch" | "bestBefore" | "serial" | "fragile" | "packUnits";
type ExtraDef = { label: string; group: string; align?: "right" } & (
  | { kind: "text"; col: ColKey; placeholder: string; mono?: boolean; numeric?: boolean; suffix?: string; width: string; issue?: string }
  | { kind: "bool"; field: "batchManaged" | "bestBeforeManaged" | "serialManaged" | "fragile"; get: (p: ProductItem) => boolean }
  | { kind: "view"; get: (p: ProductItem) => string }
);
const EXTRA: Record<ExtraKey, ExtraDef> = {
  name: { label: msg("Name"), group: "Stammdaten", kind: "text", col: "name", placeholder: msg("Name"), width: "w-56", issue: "name" },
  mpn: { label: msg("Hersteller-Art.-Nr."), group: "Stammdaten", kind: "text", col: "mpn", placeholder: msg("MPN"), mono: true, width: "w-36", issue: "identifiers.mpn" },
  manufacturer: { label: msg("Hersteller"), group: "Stammdaten", kind: "text", col: "manufacturer", placeholder: msg("Hersteller"), width: "w-36", issue: "identifiers.manufacturer" },
  netWeight: { label: msg("Nettogewicht"), group: "Logistik", align: "right", kind: "text", col: "netWeight", placeholder: msg("kg"), suffix: "kg", numeric: true, width: "w-24", issue: "weight.netKg" },
  packUnits: { label: msg("Gebinde"), group: "Logistik", kind: "view", get: (p) => (p.packUnits ?? []).map((u) => u.name).join(" · ") },
  origin: { label: msg("Ursprungsland"), group: "Zoll", kind: "text", col: "origin", placeholder: msg("z. B. DE"), mono: true, width: "w-20", issue: "customs.countryOfOrigin" },
  hs: { label: msg("Zolltarifnummer"), group: "Zoll", kind: "text", col: "hs", placeholder: msg("Ziffern"), mono: true, width: "w-32", issue: "customs.hsCode" },
  customsValue: { label: msg("Zollwert"), group: "Zoll", align: "right", kind: "text", col: "customsValue", placeholder: "€", suffix: "€", numeric: true, width: "w-24", issue: "customs.customsValue" },
  customsDesc: { label: msg("Zollbeschreibung"), group: "Zoll", kind: "text", col: "customsDesc", placeholder: msg("Warenbeschreibung"), width: "w-48", issue: "customs.customsDescription" },
  batch: { label: msg("Charge"), group: "Handling", kind: "bool", field: "batchManaged", get: (p) => !!p.handling.batchManaged },
  bestBefore: { label: msg("MHD"), group: "Handling", kind: "bool", field: "bestBeforeManaged", get: (p) => !!p.handling.bestBeforeManaged },
  serial: { label: msg("Seriennr."), group: "Handling", kind: "bool", field: "serialManaged", get: (p) => !!p.handling.serialManaged },
  fragile: { label: msg("Zerbrechlich"), group: "Handling", kind: "bool", field: "fragile", get: (p) => !!p.handling.fragile },
};
// Nettogewicht nicht mehr wählbar: ist immer gleich dem Gewicht
const EXTRA_ORDER: ExtraKey[] = ["name", "mpn", "manufacturer", "packUnits", "origin", "hs", "customsValue", "customsDesc", "batch", "bestBefore", "serial", "fragile"];
const DEFAULT_EXTRAS: ExtraKey[] = ["origin", "hs"];

/** Auswahl der Zusatzspalten im Bearbeitungsmodus */
function ColumnPicker({ value, onChange }: { value: ExtraKey[]; onChange: (v: ExtraKey[]) => void }) {
  const groups = [...new Set(EXTRA_ORDER.map((k) => EXTRA[k].group))];
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button size="sm" variant="ghost" icon={<SlidersHorizontal className="size-3.5" />} className="ml-auto">{t("Spalten")}{value.length ? ` (${value.length})` : ""}</Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={6} className="z-50 w-64 max-h-[70vh] overflow-y-auto rounded-xl border border-border bg-surface p-1.5 shadow-xl animate-pop-in">
          {groups.map((g) => (
            <div key={g}>
              <DropdownMenu.Label className="px-2 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-fg-subtle">{g}</DropdownMenu.Label>
              {EXTRA_ORDER.filter((k) => EXTRA[k].group === g).map((k) => (
                <DropdownMenu.CheckboxItem
                  key={k}
                  checked={value.includes(k)}
                  onSelect={(e) => e.preventDefault()}
                  onCheckedChange={(c) => onChange(c ? [...value, k] : value.filter((x) => x !== k))}
                  className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] outline-none cursor-pointer data-[highlighted]:bg-surface-2"
                >
                  <span className={cx("size-4 grid place-items-center rounded border", value.includes(k) ? "bg-ink border-ink text-ink-fg" : "border-border")}>{value.includes(k) && <Check className="size-3" />}</span>
                  {t(EXTRA[k].label)}
                </DropdownMenu.CheckboxItem>
              ))}
            </div>
          ))}
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          <DropdownMenu.Item onSelect={() => onChange(DEFAULT_EXTRAS)} className="rounded-lg px-2 py-1.5 text-[12.5px] text-fg-muted outline-none cursor-pointer data-[highlighted]:bg-surface-2">{t("Standard wiederherstellen")}</DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/** Gebinde für alle ausgewählten Artikel hinzufügen, ersetzen oder entfernen (EAN je Artikel bleibt bzw. wird am Artikel ergänzt) */
function PackUnitBulkDialog({ open, onOpenChange, count, onApply }: { open: boolean; onOpenChange: (o: boolean) => void; count: number; onApply: (mode: "add" | "replace" | "remove", unit: { name: string; quantity: number }) => Promise<void> }) {
  const [mode, setMode] = useState<"add" | "replace" | "remove">("add");
  const [name, setName] = useState("");
  const [qty, setQty] = useState("");
  const [busy, setBusy] = useState(false);
  const q = Number(qty);
  const valid = name.trim().length > 0 && (mode === "remove" || (Number.isInteger(q) && q >= 2));
  const apply = async () => {
    setBusy(true);
    try {
      await onApply(mode, { name: name.trim(), quantity: mode === "remove" ? 2 : q });
      onOpenChange(false);
      setName("");
      setQty("");
    } catch {
      toast.error("Nicht gespeichert");
    } finally {
      setBusy(false);
    }
  };
  const MODES = [
    { key: "add", label: msg("Hinzufügen"), hint: msg("zu vorhandenen Gebinden (gleichnamiges wird ersetzt)") },
    { key: "replace", label: msg("Ersetzen"), hint: msg("alle Gebinde durch dieses ersetzen") },
    { key: "remove", label: msg("Entfernen"), hint: msg("Gebinde mit diesem Namen entfernen") },
  ] as const;
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 animate-fade-in" />
        <Dialog.Content className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(460px,calc(100vw-32px))] rounded-2xl border border-border bg-surface p-5 shadow-2xl animate-pop-in">
          <Dialog.Title className="text-[16px] font-semibold">{t("Gebinde für")} {count} {t("Artikel")}</Dialog.Title>
          <Dialog.Description className="text-fg-muted mt-1 text-[13px]">{t("Z. B. „Karton 20“ für eine ganze Produktfamilie. Eigene EANs je Gebinde am Artikel ergänzen – sie bleiben beim Ändern erhalten.")}</Dialog.Description>
          <div className="mt-4 grid grid-cols-3 gap-1.5">
            {MODES.map((m) => (
              <button key={m.key} onClick={() => setMode(m.key)} title={t(m.hint)} className={cx("h-9 px-3 rounded-lg border text-[13px]", mode === m.key ? "border-ink bg-surface-2 font-medium" : "border-border hover:bg-surface-2")}>{t(m.label)}</button>
            ))}
          </div>
          <p className="mt-1.5 text-[12px] text-fg-subtle">{t(MODES.find((m) => m.key === mode)!.hint)}</p>
          <div className="mt-3 grid grid-cols-[1fr_96px] gap-2">
            <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={t("Name, z. B. Karton 20")} />
            {mode !== "remove" && <Input value={qty} onChange={(e) => setQty(e.target.value.replace(/\D/g, ""))} placeholder={t("Stück")} className="text-center" />}
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Dialog.Close asChild><Button variant="ghost">{t("Abbrechen")}</Button></Dialog.Close>
            <Button variant="primary" disabled={!valid} loading={busy} onClick={() => void apply()}>{t("Für")} {count} {t("Artikel übernehmen")}</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Herunterziehen wie in Excel: Wert der Startzelle in alle überstrichenen Zeilen derselben Spalte */
export interface FillDrag { col: ColKey; from: number; to: number; value: string }

/** Kompakter Filter als Auswahlmenü (gleiches Aussehen wie das restliche Portal, auch im Dark Mode) */
/** Probleme je Regel zusammenfassen (eine Regel kann als Fehler und als Hinweis vorkommen), Fehler zuerst */
function problemOptions(issues: { ruleId: string; severity: string; count: number }[]) {
  const byRule = new Map<string, { ruleId: string; count: number; error: boolean }>();
  for (const i of issues) {
    const e = byRule.get(i.ruleId) ?? { ruleId: i.ruleId, count: 0, error: false };
    e.count += i.count;
    e.error ||= i.severity === "error";
    byRule.set(i.ruleId, e);
  }
  return [...byRule.values()]
    .sort((a, b) => (a.error === b.error ? b.count - a.count : a.error ? -1 : 1))
    .slice(0, 12)
    .map((i) => ({ value: i.ruleId, label: ruleLabel(i.ruleId), count: i.count, tone: i.error ? ("error" as const) : ("warning" as const) }));
}

function FilterSelect({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string; count?: number; tone?: "error" | "warning" }[] }) {
  const current = options.find((o) => o.value === value);
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button className={cx("inline-flex items-center gap-1.5 h-8 pl-2.5 pr-2 rounded-lg border text-[12.5px] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ink/30", value ? "border-ink bg-surface-2" : "border-border hover:bg-surface-2")}>
          <span className="text-fg-subtle">{t(label)}:</span>
          <span className="font-medium max-w-[200px] truncate">{current?.label ?? t("Alle")}</span>
          {current?.count != null && <span className="text-fg-subtle tabular-nums">{current.count}</span>}
          <ChevronDown className="size-3.5 opacity-60" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="start" sideOffset={6} className="z-[60] min-w-[240px] max-h-[60vh] overflow-auto rounded-xl border border-border bg-surface p-1 shadow-xl animate-fade-in">
          {[{ value: "", label: msg("Alle") } as (typeof options)[number], ...options].map((o) => (
            <DropdownMenu.Item key={o.value || "_all"} onSelect={() => onChange(o.value)} className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-[13px] outline-none cursor-pointer data-[highlighted]:bg-surface-2">
              <Check className={cx("size-3.5 shrink-0", o.value === value ? "opacity-100" : "opacity-0")} />
              {o.tone && <span className={cx("size-1.5 rounded-full shrink-0", o.tone === "error" ? "bg-danger" : "bg-warning")} />}
              <span className="flex-1">{t(o.label)}</span>
              {o.count != null && <span className="text-fg-subtle tabular-nums text-[12px]">{o.count}</span>}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function Th({ children, onClick, sorted, className }: { children: ReactNode; onClick?: () => void; sorted?: "asc" | "desc"; className?: string }) {
  return (
    <th className={cx("h-9 px-3 font-medium whitespace-nowrap", className)} aria-sort={sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : undefined}>
      {onClick ? (
        <button onClick={onClick} className="inline-flex items-center gap-1 hover:text-fg">
          {children}
          {sorted === "asc" ? <ArrowUp className="size-3" /> : sorted === "desc" ? <ArrowDown className="size-3" /> : null}
        </button>
      ) : (
        children
      )}
    </th>
  );
}

function SelectBox({ checked, onChange, label }: { checked: boolean | "indeterminate"; onChange: (v: boolean) => void; label: string }) {
  return (
    <Checkbox.Root
      checked={checked}
      onCheckedChange={(v) => onChange(v === true)}
      onClick={(e) => e.stopPropagation()}
      aria-label={label}
      className="size-4 rounded border border-border-strong bg-surface grid place-items-center data-[state=checked]:bg-ink data-[state=checked]:border-ink data-[state=indeterminate]:bg-ink data-[state=indeterminate]:border-ink"
    >
      <Checkbox.Indicator className="text-ink-fg">{checked === "indeterminate" ? <Minus className="size-3" /> : <Check className="size-3" />}</Checkbox.Indicator>
    </Checkbox.Root>
  );
}

const BACKEND_FILTERS: { key: BackendFilter; label: string; Icon: typeof Check; tone: string }[] = [
  { key: "released", label: msg("Freigegeben"), Icon: CheckCircle2, tone: "text-success" },
  { key: "transferred", label: msg("Freigabe fehlt"), Icon: Clock3, tone: "text-warning" },
  { key: "missing", label: msg("Nicht übertragen"), Icon: CircleDashed, tone: "text-fg-subtle" },
  { key: "error", label: msg("Fehler"), Icon: XCircle, tone: "text-danger" },
];

/** Lager-Stand eines Artikels: übertragen und für Logiful (Fulfiller) freigegeben? */
function BackendCell({ b }: { b: ProductItem["backend"] }) {
  if (!b) return <span className="text-fg-subtle text-[12px]">–</span>;
  const meta = {
    released: { label: msg("Freigegeben"), Icon: CheckCircle2, cls: "text-success", tip: "Im Lager angelegt und für Logiful freigegeben" },
    transferred: { label: msg("Freigabe fehlt"), Icon: Clock3, cls: "text-warning", tip: b.message ?? "Im Lager angelegt, aber noch nicht für Logiful freigegeben – wird stündlich geprüft" },
    pending: { label: msg("Ausstehend"), Icon: CircleDashed, cls: "text-fg-subtle", tip: b.message ?? "Übertragung ausstehend" },
    blocked: { label: msg("Nicht übertragen"), Icon: CircleDashed, cls: "text-fg-muted", tip: b.message ?? "Stammdaten unvollständig – wird nach Ergänzung automatisch übertragen" },
    error: { label: msg("Fehler"), Icon: XCircle, cls: "text-danger", tip: b.message ?? "Lager hat den Artikel abgelehnt" },
  }[b.status];
  return (
    <Tip content={<span>{meta.tip}{b.at ? <span className="block text-fg-subtle mt-0.5">{fmtRelative(b.at)}</span> : null}</span>}>
      <span className={cx("inline-flex items-center gap-1.5 text-[12.5px]", meta.cls)}><meta.Icon className="size-3.5" /> {t(meta.label)}</span>
    </Tip>
  );
}

/** Streifenfarben für EAN-Gruppen (Filter „Doppelte EAN“) – abwechselnd, damit Nachbargruppen unterscheidbar sind */
const GROUP_STRIPES = ["#f59e0b", "#38bdf8", "#a78bfa", "#34d399", "#fb7185", "#22d3ee"];

function ProductRow({ p, index, editMode, extras, fill, onFillStart, onFillOver, onFillToEnd, selected, onSelect, onOpen, onPatch, active, gtinGroup, onOpenId }: { gtinGroup?: number; onOpenId?: (id: string) => void; p: ProductItem; index: number; editMode: boolean; extras: ExtraKey[]; fill: FillDrag | null; onFillStart: (col: ColKey, value: string) => void; onFillOver: () => void; onFillToEnd: (col: ColKey, value: string) => void; selected: boolean; onSelect: (v: boolean) => void; onOpen: () => void; onPatch: (patch: ProductPatch) => void; active: boolean }) {
  const inFill = (col: ColKey) => !!fill && fill.col === col && index >= Math.min(fill.from, fill.to) && index <= Math.max(fill.from, fill.to);
  /** Zelle einer bearbeitbaren Spalte: Lesen, im Bearbeitungsmodus Ändern + Ausfüllkästchen */
  const cell = (col: ColKey, opts: { placeholder: string; suffix?: string; align?: "right"; mono?: boolean; numeric?: boolean; width: string; invalid?: boolean }) => {
    const value = COLS[col].get(p);
    return (
      <div className={cx("relative group/cell", inFill(col) && "outline outline-2 outline-dashed outline-ink/60 rounded-md")}>
        <InlineEdit
          value={value}
          readOnly={!editMode}
          {...opts}
          onSave={(v) => { const r = COLS[col].toPatch(v); if (typeof r === "string") toast.error(r); else onPatch(r); }}
        />
        {editMode && (
          <span
            role="button"
            aria-label={t("{label} nach unten übernehmen", { label: COLS[col].label })}
            title={t("Herunterziehen: Wert in weitere Zeilen übernehmen · Doppelklick: bis zum Ende der Seite")}
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onFillStart(col, value); }}
            onDoubleClick={(e) => { e.stopPropagation(); onFillToEnd(col, value); }}
            onClick={(e) => e.stopPropagation()}
            className="absolute -bottom-1 -right-1 size-2.5 rounded-[2px] bg-ink border border-surface cursor-crosshair opacity-0 group-hover/cell:opacity-100"
          />
        )}
      </div>
    );
  };
  const errors = p.validation.issues.filter((i) => i.severity === "error");
  const warnings = p.validation.issues.filter((i) => i.severity === "warning");
  const first = errors[0] ?? warnings[0];
  const hasIssue = (field: string) => p.validation.issues.some((i) => i.field.startsWith(field) && i.severity !== "info");
  const d = p.dimensions;

  return (
    <tr
      onClick={onOpen}
      onMouseEnter={onFillOver}
      className={cx("group border-b border-border last:border-0 cursor-pointer transition-colors", active ? "bg-surface-2 shadow-[inset_2px_0_0_var(--color-ink)]" : selected ? "bg-surface-2/70" : "hover:bg-surface-2/50")}
      style={gtinGroup != null && !active ? { boxShadow: `inset 3px 0 0 ${GROUP_STRIPES[gtinGroup % GROUP_STRIPES.length]}` } : undefined}
    >
      <td className="pl-4 py-2.5 w-10"><SelectBox checked={selected} onChange={onSelect} label={t("{sku} auswählen", { sku: p.sku })} /></td>
      <td className="px-3 py-2.5 min-w-[260px]">
        <div className="font-medium truncate max-w-[340px]">{p.name}</div>
        <div className="font-mono text-[11.5px] text-fg-muted flex items-center gap-1.5">
          {p.sku}
          {p.status === "draft" && <Badge>{t("Entwurf")}</Badge>}
          {p.bundle?.components.length ? <Badge tone="info">{t("Set")}</Badge> : null}
        </div>
      </td>
      <td className="px-3 py-2.5">
        {cell("gtin", { placeholder: msg("EAN ergänzen"), invalid: hasIssue("identifiers.gtin"), mono: true, width: "w-36" })}
        {!!p.sameGtin?.length && <SameGtin items={p.sameGtin} onOpen={onOpenId} />}
      </td>
      <td className="px-3 py-2.5 text-right">
        {cell("weight", { placeholder: msg("kg"), suffix: "kg", invalid: hasIssue("weight"), align: "right", numeric: true, width: "w-24" })}
      </td>
      <td className="px-3 py-2.5">
        {cell("dims", { placeholder: msg("L × B × H cm"), suffix: "cm", invalid: hasIssue("dimensions"), width: "w-40" })}
      </td>
      {editMode &&
        extras.map((k) => {
          const def = EXTRA[k];
          if (def.kind === "text") return <td key={k} className={cx("px-3 py-2.5", def.align === "right" && "text-right")}>{cell(def.col, { placeholder: def.placeholder, ...(def.mono ? { mono: true } : {}), ...(def.numeric ? { numeric: true } : {}), ...(def.suffix ? { suffix: def.suffix } : {}), ...(def.align ? { align: def.align } : {}), width: def.width, invalid: def.issue ? hasIssue(def.issue) : false })}</td>;
          if (def.kind === "bool")
            return (
              <td key={k} className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                <input type="checkbox" className="size-4 accent-ink cursor-pointer" checked={def.get(p)} onChange={(e) => onPatch({ [def.field]: e.target.checked } as ProductPatch)} aria-label={`${def.label} – ${p.sku}`} />
              </td>
            );
          return <td key={k} className="px-3 py-2.5 text-[12.5px] text-fg-muted max-w-[200px] truncate" title={t("Gebinde am Artikel bearbeiten")}>{def.get(p) || "—"}</td>;
        })}
      {!editMode && <td className="px-3 py-2.5"><ScoreBar score={p.validation.completenessScore} /></td>}
      <td className="px-3 py-2.5 max-w-[280px]">
        {first ? (
          <Tip content={<ul className="space-y-1">{p.validation.issues.filter((i) => i.severity !== "info").map((i, k) => <li key={k}>• {serverText(i.message)}</li>)}</ul>}>
            <span className="flex items-center gap-1.5 min-w-0">
              <SeverityIcon severity={first.severity} className="size-3.5" />
              <span className="truncate text-[12.5px]">{ruleLabel(first.ruleId)}</span>
              {errors.length + warnings.length > 1 && <span className="text-[11.5px] text-fg-subtle shrink-0">+{errors.length + warnings.length - 1}</span>}
            </span>
          </Tip>
        ) : (
          <span className="flex items-center gap-1.5 text-[12.5px] text-success"><Check className="size-3.5" /> {t("Bereit fürs Lager")}</span>
        )}
      </td>
      {!editMode && (
        <>
          <td className="px-3 py-2.5 whitespace-nowrap"><BackendCell b={p.backend} /></td>
          <td className="pr-4 pl-3 py-2.5 text-right text-[12px] text-fg-muted whitespace-nowrap hidden 2xl:table-cell">{fmtRelative(p.updatedAt)}</td>
        </>
      )}
    </tr>
  );
}

/** Zelle zum direkten Bearbeiten: Klick → Eingabe, Enter speichert, Esc verwirft */
function InlineEdit({ value, placeholder, onSave, invalid, suffix, align, mono, numeric, width, readOnly }: { value: string; placeholder: string; onSave: (v: string) => void; invalid?: boolean; suffix?: string; align?: "right"; mono?: boolean; numeric?: boolean; width: string; readOnly?: boolean }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => setDraft(value), [value]);
  useEffect(() => {
    if (editing) ref.current?.select();
  }, [editing]);

  const commit = () => {
    setEditing(false);
    if (draft.trim() !== value) onSave(draft.trim());
  };

  // ohne Bearbeitungsmodus nur anzeigen (Klick öffnet den Artikel)
  if (readOnly)
    return (
      <span className={cx("inline-flex items-center h-7 max-w-full", align === "right" && "justify-end", mono && "font-mono text-[12px]", width)}>
        {value ? (
          <span className={cx("truncate tabular-nums", invalid && "text-danger")}>{value}{suffix && <span className="text-fg-subtle ml-1">{suffix}</span>}</span>
        ) : (
          <span className={cx("truncate", invalid ? "text-danger/80" : "text-fg-subtle")}>—</span>
        )}
      </span>
    );

  if (editing)
    return (
      <div onClick={(e) => e.stopPropagation()} className={cx("inline-flex", width)}>
        <Input
          ref={ref}
          value={draft}
          inputMode={numeric ? "decimal" : undefined}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") {
              setDraft(value);
              setEditing(false);
            }
          }}
          suffix={suffix}
          className={cx("h-7 w-full", mono && "font-mono text-[12px]", align === "right" && "[&_input]:text-right")}
        />
      </div>
    );

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        setEditing(true);
      }}
      className={cx(
        "inline-flex items-center gap-1 h-7 px-2 -mx-2 rounded-md border border-transparent hover:border-border hover:bg-surface transition-colors max-w-full",
        align === "right" && "justify-end",
        mono && "font-mono text-[12px]",
        width,
      )}
      title={t("Klicken zum Bearbeiten")}
    >
      {value ? (
        <span className={cx("truncate tabular-nums", invalid && "text-danger")}>
          {value}
          {suffix && <span className="text-fg-subtle ml-1">{suffix}</span>}
        </span>
      ) : (
        <span className={cx("truncate", invalid ? "text-danger/80" : "text-fg-subtle")}>{invalid ? `+ ${placeholder}` : "—"}</span>
      )}
    </button>
  );
}

const BULK_FIELDS = [
  { key: "countryOfOrigin", label: msg("Ursprungsland"), placeholder: msg("z. B. DE"), kind: "text" },
  { key: "hsCode", label: msg("Zolltarifnummer"), placeholder: "z. B. 33049900", kind: "text" },
  { key: "grossWeightKg", label: msg("Gewicht (kg)"), placeholder: "z. B. 0,25", kind: "number" },
  { key: "batchManaged", label: msg("Chargenpflichtig"), kind: "bool" },
  { key: "bestBeforeManaged", label: msg("MHD-pflichtig"), kind: "bool" },
  { key: "fragile", label: msg("Zerbrechlich"), kind: "bool" },
] as const;

function BulkEditDialog({ open, onOpenChange, count, onApply }: { open: boolean; onOpenChange: (o: boolean) => void; count: number; onApply: (p: ProductPatch) => Promise<void> }) {
  const [field, setField] = useState<(typeof BULK_FIELDS)[number]["key"]>("countryOfOrigin");
  const [value, setValue] = useState("");
  const [bool, setBool] = useState(true);
  const [busy, setBusy] = useState(false);
  const def = BULK_FIELDS.find((f) => f.key === field)!;

  const apply = async () => {
    const v = def.kind === "bool" ? bool : def.kind === "number" ? Number(value.replace(",", ".")) : value.trim().toUpperCase() || null;
    if (def.kind === "number" && (Number.isNaN(v) || (v as number) <= 0)) return toast.error("Bitte eine positive Zahl eingeben");
    setBusy(true);
    try {
      await onApply({ [field]: v } as ProductPatch);
      onOpenChange(false);
      setValue("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 animate-fade-in" />
        <Dialog.Content className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(440px,calc(100vw-32px))] rounded-2xl border border-border bg-surface p-5 shadow-2xl animate-pop-in">
          <Dialog.Title className="text-[16px] font-semibold">{t("Feld für")} {count} {t("Artikel setzen")}</Dialog.Title>
          <Dialog.Description className="text-fg-muted mt-1">{t("Der Wert wird übernommen, die Artikel werden sofort neu geprüft und – wenn vollständig – ans Lager übertragen.")}</Dialog.Description>
          <div className="mt-4 grid grid-cols-2 gap-1.5">
            {BULK_FIELDS.map((f) => (
              <button key={f.key} onClick={() => setField(f.key)} className={cx("h-9 px-3 rounded-lg border text-left text-[13px]", field === f.key ? "border-ink bg-surface-2 font-medium" : "border-border hover:bg-surface-2")}>
                {t(f.label)}
              </button>
            ))}
          </div>
          <div className="mt-4">
            {def.kind === "bool" ? (
              <div className="inline-flex p-0.5 rounded-lg bg-surface-2 border border-border">
                {[true, false].map((b) => (
                  <button key={String(b)} onClick={() => setBool(b)} className={cx("h-7 px-4 rounded-md text-[13px]", bool === b ? "bg-surface shadow-sm font-medium" : "text-fg-muted")}>
                    {b ? t("Ja") : t("Nein")}
                  </button>
                ))}
              </div>
            ) : (
              <Input autoFocus value={value} onChange={(e) => setValue(e.target.value)} placeholder={t(def.placeholder)} onKeyDown={(e) => e.key === "Enter" && void apply()} />
            )}
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Dialog.Close asChild><Button variant="ghost">{t("Abbrechen")}</Button></Dialog.Close>
            <Button variant="primary" onClick={() => void apply()} loading={busy}>{t("Für")} {count} {t("Artikel übernehmen")}</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

